import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, ButtonGroup } from 'react-bootstrap';
import { UploadCloud, FileSliders, CheckCircle2, Download, Calculator } from 'lucide-react';
import * as XLSX from 'xlsx';

const MFG_RATES = [
  { min: 1.00, max: 1.49, mfgRate: 1300, igiRate: 11 },
  { min: 1.50, max: 1.99, mfgRate: 1300, igiRate: 15 },
  { min: 2.00, max: 2.99, mfgRate: 1150, igiRate: 21 },
  { min: 3.00, max: 3.99, mfgRate: 1150, igiRate: 26 },
  { min: 4.00, max: 4.99, mfgRate: 1150, igiRate: 32 },
  { min: 5.00, max: 5.99, mfgRate: 950, igiRate: 52 },
  { min: 6.00, max: 7.99, mfgRate: 950, igiRate: 65 },
  { min: 8.00, max: 9.99, mfgRate: 950, igiRate: 78 },
  { min: 10.00, max: 11.99, mfgRate: 950, igiRate: 115 },
];

const getRates = (weight) => {
  const matchingRange = MFG_RATES.find(r => weight >= r.min && weight <= r.max);
  return matchingRange ? matchingRange : { mfgRate: 0, igiRate: 0 };
};

const calculateSingleRow = (originalWeight, exchangeRate) => {
  const newRow = {};

  if (!isNaN(originalWeight) && originalWeight > 0) {
    const currentExchangeRate = parseFloat(exchangeRate) || 1;
    const ratesForOriginal = getRates(originalWeight);
    const igiRateUSD = ratesForOriginal.igiRate;
    const igiCostINR = igiRateUSD * currentExchangeRate;

    const newWeight = originalWeight * 1.30;
    newRow['New Weight'] = newWeight.toFixed(2);
    
    const ratesForNewWeight = getRates(newWeight);
    newRow['New MFG Rate'] = ratesForOriginal.mfgRate;
    const newMfgCost = originalWeight * ratesForOriginal.mfgRate;
    newRow['New MFG Cost'] = newMfgCost.toFixed(2);
    
    const hphtWeight = originalWeight * 1.20;
    newRow['HPHT Weight'] = hphtWeight.toFixed(2);
    
    const hphtFixedRate = 425;
    newRow['HPHT MFG Rate'] = hphtFixedRate;
    const hphtCost = hphtWeight * hphtFixedRate;
    newRow['HPHT Cost'] = hphtCost.toFixed(2);
    
    newRow['IGI Rate (USD)'] = igiRateUSD;
    newRow['IGI Cost (INR)'] = igiCostINR.toFixed(2);

    const finalCost = newMfgCost + hphtCost + igiCostINR;
    newRow['Final Cost'] = finalCost.toFixed(2);
  } else {
    newRow['New Weight'] = '-';
    newRow['New MFG Rate'] = '-';
    newRow['New MFG Cost'] = '-';
    newRow['HPHT Weight'] = '-';
    newRow['HPHT MFG Rate'] = '-';
    newRow['HPHT Cost'] = '-';
    newRow['IGI Rate (USD)'] = '-';
    newRow['IGI Cost (INR)'] = '-';
    newRow['Final Cost'] = '-';
  }

  return newRow;
};

const CalculateCost = () => {
  const [mode, setMode] = useState('excel'); // 'excel' or 'manual'
  
  const [file, setFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [mapping, setMapping] = useState({ weight: '' });
  const [exchangeRate, setExchangeRate] = useState(83.5);
  const [calculatedData, setCalculatedData] = useState([]);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // Manual mode state
  const [roughWeight, setRoughWeight] = useState('');
  const [roughRate, setRoughRate] = useState('');
  const [manualDiamonds, setManualDiamonds] = useState([{ weight: '' }]);
  const [manualResult, setManualResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        
        if (data.length > 0) {
          const headers = data[0].filter(h => h && h.trim() !== "");
          setExcelHeaders(headers);
          
          const rows = XLSX.utils.sheet_to_json(ws);
          setExcelData(rows);

          // Auto map
          const initialMapping = { weight: '' };
          headers.forEach(h => {
            const lowerH = h.toLowerCase();
            if (lowerH.includes('weight') || lowerH.includes('carat')) initialMapping.weight = h;
          });
          setMapping(initialMapping);
          setStep(2);
          setError('');
        }
      } catch (err) {
        setError('Failed to parse Excel file. Ensure it is a valid .xlsx or .xls file.');
        console.error(err);
      }
    };
    reader.readAsBinaryString(selectedFile);
  };

  const processCalculations = () => {
    if (!mapping.weight) {
      setError('Please map the Weight column before proceeding.');
      return;
    }
    
    setError('');
    
    const processed = excelData.map(row => {
      const originalWeight = parseFloat(row[mapping.weight]);
      const calculations = calculateSingleRow(originalWeight, exchangeRate);
      return { ...row, ...calculations };
    });

    setCalculatedData(processed);
    setStep(3);
  };

  const handleManualCalculate = () => {
    const rWeight = parseFloat(roughWeight) || 0;
    const rRate = parseFloat(roughRate) || 0;
    const rCost = rWeight * rRate;

    let totalDiamondsWeight = 0;
    let totalDiamondsCost = 0;
    const diamondsData = [];

    let hasError = false;

    manualDiamonds.forEach((d, idx) => {
      if (!d.weight) return;
      const w = parseFloat(d.weight);
      if (isNaN(w) || w <= 0) {
        hasError = true;
        return;
      }

      const ratesOrig = getRates(w);
      const igiUSD = ratesOrig.igiRate * 1.09;

      const currentExchangeRate = parseFloat(exchangeRate) || 1;
      const newWeight = w * 1.30;
      const mfgRateINR = ratesOrig.mfgRate;
      const mfgCostINR = w * mfgRateINR; 
      const mfgCostUSD = mfgCostINR / currentExchangeRate;

      const hphtWeight = w * 1.20;
      const hphtRateINR = 425;
      const hphtCostINR = hphtWeight * hphtRateINR;
      const hphtCostUSD = hphtCostINR / currentExchangeRate;

      const finalCostUSD = mfgCostUSD + hphtCostUSD + igiUSD;

      totalDiamondsWeight += w;
      totalDiamondsCost += finalCostUSD;

      diamondsData.push({
        id: idx + 1,
        weight: w,
        newWeight,
        mfgRateINR,
        mfgCostINR,
        mfgCostUSD,
        hphtWeight,
        hphtRateINR,
        hphtCostINR,
        hphtCostUSD,
        igiUSD,
        finalCostUSD
      });
    });

    if (hasError) {
      setError('Please enter valid weights for all diamonds.');
      return;
    }

    if (diamondsData.length === 0 && rWeight === 0) {
      setError('Please enter rough details or at least one diamond.');
      return;
    }

    setError('');
    
    setManualResult({
      rWeight,
      rCost,
      diamondsData,
      totalDiamondsWeight,
      totalDiamondsCost,
      totalWeight: rWeight + totalDiamondsWeight,
      totalCost: rCost + totalDiamondsCost
    });
    setStep(3);
  };

  const addDiamond = () => setManualDiamonds([...manualDiamonds, { weight: '' }]);
  const removeDiamond = (index) => setManualDiamonds(manualDiamonds.filter((_, i) => i !== index));
  const updateDiamond = (index, val) => {
    const newD = [...manualDiamonds];
    newD[index].weight = val;
    setManualDiamonds(newD);
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(calculatedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Calculated Costs");
    XLSX.writeFile(wb, "calculated_diamond_costs.xlsx");
  };

  const resetAll = () => {
    setFile(null);
    setExcelHeaders([]);
    setExcelData([]);
    setCalculatedData([]);
    setStep(1);
    setMapping({ weight: '' });
    setRoughWeight('');
    setRoughRate('');
    setManualDiamonds([{ weight: '' }]);
    setManualResult(null);
    setError('');
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetAll();
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-serif)' }}>
          Calculate Cost
        </h2>
        {step > 1 && mode === 'excel' && (
          <Button variant="outline-secondary" onClick={resetAll}>
            Start Over
          </Button>
        )}
        {step === 3 && mode === 'manual' && (
          <Button variant="outline-secondary" onClick={resetAll}>
            Clear
          </Button>
        )}
      </div>

      <div className="mb-4">
        <ButtonGroup className="shadow-sm">
          <Button 
            variant={mode === 'excel' ? 'primary' : 'light'} 
            onClick={() => handleModeChange('excel')}
            className={mode === 'excel' ? '' : 'text-muted border'}
          >
            <UploadCloud size={18} className="me-2" />
            Bulk Import (Excel)
          </Button>
          <Button 
            variant={mode === 'manual' ? 'primary' : 'light'} 
            onClick={() => handleModeChange('manual')}
            className={mode === 'manual' ? '' : 'text-muted border'}
          >
            <Calculator size={18} className="me-2" />
            Manual Input
          </Button>
        </ButtonGroup>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* MANUAL MODE */}
      {mode === 'manual' && step !== 3 && (
        <Card className="border-0 shadow-sm p-4">
          <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
            <Calculator size={20} className="text-primary" />
            Enter Details
          </h5>
          <Row className="g-4 mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-medium">Rough Weight</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  value={roughWeight} 
                  onChange={(e) => setRoughWeight(e.target.value)}
                  placeholder="e.g., 10.5"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-medium">Rough Rate ($)</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  value={roughRate} 
                  onChange={(e) => setRoughRate(e.target.value)}
                  placeholder="e.g., 200"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-medium">USD to INR Exchange Rate <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="fw-bold mb-3">Polished Diamonds</h6>
          {manualDiamonds.map((d, index) => (
            <Row className="g-3 mb-2 align-items-center" key={index}>
              <Col md={4}>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  value={d.weight} 
                  onChange={(e) => updateDiamond(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addDiamond();
                    }
                  }}
                  placeholder={`Diamond ${index + 1} Weight`}
                />
              </Col>
              <Col md={2}>
                {manualDiamonds.length > 1 && (
                  <Button variant="outline-danger" size="sm" onClick={() => removeDiamond(index)}>
                    Remove
                  </Button>
                )}
              </Col>
            </Row>
          ))}
          <div className="mb-4 mt-2">
            <Button variant="outline-primary" size="sm" onClick={addDiamond}>
              + Add Another Diamond
            </Button>
          </div>

          <div className="d-flex justify-content-end">
            <Button 
              size="lg"
              style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
              onClick={handleManualCalculate}
            >
              Calculate
            </Button>
          </div>
        </Card>
      )}

      {/* EXCEL MODE: STEP 1 */}
      {mode === 'excel' && step === 1 && (
        <Card className="border-0 shadow-sm p-5 text-center">
          <div className="mb-4">
            <UploadCloud size={48} className="text-primary mb-3" />
            <h4 className="fw-bold">Upload Excel File</h4>
            <p className="text-muted">Upload an Excel file containing diamond weights to calculate manufacturing costs.</p>
          </div>
          <div className="d-flex justify-content-center">
            <Form.Control 
              type="file" 
              accept=".xlsx, .xls" 
              onChange={handleFileChange} 
              className="w-50"
              size="lg"
            />
          </div>
        </Card>
      )}

      {/* EXCEL MODE: STEP 2 */}
      {mode === 'excel' && step === 2 && (
        <Card className="border-0 shadow-sm p-4">
          <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
            <FileSliders size={20} className="text-primary" />
            Map Columns
          </h5>
          
          <Row className="mb-4 g-4">
            <Col md={6}>
              <Card className="bg-light border-0 h-100">
                <Card.Body>
                  <h6 className="fw-bold mb-3 text-dark">Data Mapping</h6>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Weight Column <span className="text-danger">*</span></Form.Label>
                    <Form.Select 
                      value={mapping.weight} 
                      onChange={(e) => setMapping(prev => ({...prev, weight: e.target.value}))}
                    >
                      <option value="">-- Select Weight Column --</option>
                      {excelHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fw-medium">USD to INR Exchange Rate <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="number" 
                      step="0.01"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card className="bg-light border-0 h-100">
                <Card.Body>
                  <h6 className="fw-bold mb-3 text-dark">Calculation Summary</h6>
                  <p className="small text-muted mb-2">
                    <strong>New Weight:</strong> Original Weight + 30%<br/>
                    <strong>New MFG Cost:</strong> New Weight * Rate Slab (Based on New Weight)<br/>
                    <strong>HPHT Weight:</strong> Original Weight + 20%<br/>
                    <strong>HPHT Cost:</strong> HPHT Weight * Rate Slab (Based on HPHT Weight)<br/>
                    <strong>IGI Cost (INR):</strong> Fixed Rate Slab (Based on Original Weight) * Exchange Rate<br/>
                    <strong>Final Cost:</strong> New MFG Cost + HPHT Cost + IGI Cost (INR)
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <Button 
              size="lg"
              style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
              onClick={processCalculations}
              disabled={!mapping.weight}
            >
              Calculate Costs
            </Button>
          </div>
        </Card>
      )}

      {/* RESULTS: STEP 3 */}
      {step === 3 && mode === 'excel' && (
        <Card className="border-0 shadow-sm p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
              <CheckCircle2 size={20} className="text-success" />
              Calculation Preview
            </h5>
            <Button 
              onClick={handleExport}
              className="d-flex align-items-center gap-2"
              style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
            >
              <Download size={18} />
              Confirm & Export to Excel
            </Button>
          </div>

          <div className="table-responsive">
            <Table striped hover className="align-middle text-center border">
              <thead className="bg-light">
                <tr>
                  <th>Row</th>
                  <th>Original Wt</th>
                  <th>New Wt</th>
                  <th>New MFG Rate</th>
                  <th>New MFG Cost</th>
                  <th>HPHT Wt</th>
                  <th>HPHT MFG Rate</th>
                  <th>HPHT Cost</th>
                  <th>IGI Rate (USD)</th>
                  <th>IGI Cost (INR)</th>
                  <th className="bg-primary text-white bg-opacity-75">Final Cost</th>
                </tr>
              </thead>
              <tbody>
                {calculatedData.slice(0, 10).map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="fw-medium">{row[mapping.weight]}</td>
                    <td>{row['New Weight']}</td>
                    <td>{row['New MFG Rate']}</td>
                    <td>{row['New MFG Cost']}</td>
                    <td>{row['HPHT Weight']}</td>
                    <td>{row['HPHT MFG Rate']}</td>
                    <td>{row['HPHT Cost']}</td>
                    <td>{row['IGI Rate (USD)']}</td>
                    <td>{row['IGI Cost (INR)']}</td>
                    <td className="fw-bold">{row['Final Cost']}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {calculatedData.length > 10 && (
              <div className="text-center text-muted small mt-2">
                Showing first 10 rows. All {calculatedData.length} rows will be exported.
              </div>
            )}
          </div>
        </Card>
      )}

      {step === 3 && mode === 'manual' && manualResult && (
        <Card className="border-0 shadow-sm p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
              <CheckCircle2 size={20} className="text-success" />
              Calculation Summary (USD)
            </h5>
          </div>

          <Row className="g-4 mb-4">
            <Col md={12}>
              <Card className="bg-light border-0 h-100">
                <Card.Body>
                  <h6 className="fw-bold mb-3">Diamonds Calculation Breakdown</h6>
                  {manualResult.diamondsData.length > 0 ? (
                    <div className="table-responsive">
                      <Table size="sm" bordered hover className="text-center bg-white mb-0 align-middle">
                        <thead className="bg-light">
                          <tr>
                            <th>#</th>
                            <th>Weight</th>
                            <th>New Wt</th>
                            <th>New MFG Rate (₹)</th>
                            <th>New MFG Cost (₹)</th>
                            <th>New MFG Cost ($)</th>
                            <th>HPHT Wt</th>
                            {/* <th>HPHT Rate (₹)</th> */}
                            <th>HPHT Cost (₹)</th>
                            <th>HPHT Cost ($)</th>
                            <th>IGI Rate+9% ($)</th>
                            <th>Final Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manualResult.diamondsData.map(d => (
                            <tr key={d.id}>
                              <td>{d.id}</td>
                              <td className="fw-medium">{d.weight.toFixed(2)}</td>
                              <td>{d.newWeight.toFixed(2)}</td>
                              <td>{d.mfgRateINR}</td>
                              <td>₹{d.mfgCostINR.toFixed(2)}</td>
                              <td>${d.mfgCostUSD.toFixed(2)}</td>
                              <td>{d.hphtWeight.toFixed(2)}</td>
                              {/* <td>{d.hphtRateINR}</td> */}
                              <td>₹{d.hphtCostINR.toFixed(2)}</td>
                              <td>${d.hphtCostUSD.toFixed(2)}</td>
                              <td>${d.igiUSD.toFixed(2)}</td>
                              <td className="fw-bold text-primary">${d.finalCostUSD.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No diamonds entered.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={12}>
              <Card className="bg-light border-0 h-100">
                <Card.Body>
                  <h6 className="fw-bold mb-3">Overall Summary</h6>
                  <Table size="sm" bordered className="text-center bg-white mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
                    <thead className="bg-light">
                      <tr>
                        <th>Category</th>
                        <th>Total Weight</th>
                        <th>Total Cost ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fw-medium text-start">Rough</td>
                        <td>{manualResult.rWeight.toFixed(2)}</td>
                        <td>${manualResult.rCost.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td className="fw-medium text-start">Polished Diamonds</td>
                        <td>{manualResult.totalDiamondsWeight.toFixed(2)}</td>
                        <td>${manualResult.totalDiamondsCost.toFixed(2)}</td>
                      </tr>
                      <tr className="table-primary fw-bold">
                        <td className="text-start">Grand Total</td>
                        <td>-</td>
                        <td>${manualResult.totalCost.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <div className="text-center mt-2">
            <Button variant="outline-primary" onClick={() => setStep(1)}>Calculate Another</Button>
          </div>
        </Card>
      )}
    </Container>
  );
};

export default CalculateCost;
